import React, { Fragment, useCallback, useEffect, useMemo, useState }  from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import ModalAdmin from '../../Components/ModalAdmin';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';
import useStorage from '../../Hooks/useStorage';

import calendarioHelper from '../../Helpers/Calendario';
import configuracionHelper from '../../Helpers/Configuracion';

const mesString = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const aInputFecha = ({ dia, mes, anio }) => (
    anio === undefined ? '' : `${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
);

const deInputFecha = valor => {
    const [anio, mes, dia] = valor.split('-').map(Number);
    return { dia, mes: mes - 1, anio, fecha: `${dia} de ${mesString[mes - 1]} del ${anio}` };
};

const Calendario = props => {
    const { editar, setEditar, index, setIndex, data, setData, recargar } = useAuth();

    const [evento, setEvento] = useState({ ...editar });
    const [nuevo, setNuevo] = useState({ color: "#000000" });

    const [calendarioPdf, setCalendarioPdf] = useState('');
    const [pdfActualizado, setPdfActualizado] = useState(null);
    const [modalPdf, setModalPdf] = useState(false);
    const [archivoPdf, setArchivoPdf] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [consulta, setConsulta] = useState('');
    const [respuestaStorage] = useStorage(consulta);

    useEffect(() => {
        obtenerDatos();
    }, []);

    useEffect(() => {
        setEvento({ ...editar });
    }, [editar]);

    useEffect(() => {
        if (respuestaStorage.action === 'agregarPdf') guardarPdf(respuestaStorage.url);
        if (respuestaStorage.action === 'eliminarSinConfirmar') setConsulta('');
    }, [respuestaStorage]);

    const obtenerDatos = async () => {
        const [resEventos, resConfiguracion] = await Promise.all([
            calendarioHelper.obtenerEventos(),
            configuracionHelper.obtenerConfiguracion()
        ]);
        if (resEventos.success) {
            setData(resEventos.data);
        } else {
            Swal.fire('Error!', 'No se pudieron obtener los eventos, codigo: ' + resEventos.code, 'error');
        }
        if (resConfiguracion.success) {
            setCalendarioPdf(resConfiguracion.data?.calendario_pdf ?? '');
            setPdfActualizado(resConfiguracion.data?.updatedAt ?? null);
        }
    }

    const cerrarModalPdf = useCallback(() => {
        setModalPdf(false);
        setArchivoPdf(null);
    }, []);

    const hayCambiosEditar = Boolean(editar._id) && JSON.stringify(evento) !== JSON.stringify(editar);
    const hayCambiosNuevo = nuevo.anio !== undefined || Boolean(nuevo.fecha) || Boolean(nuevo.evento);

    // actualizarBorrador modifica `data` en vivo para la vista previa; `editar` conserva el original
    const limpiarBorradores = () => {
        if (index !== false) setData(data => data.map((dato, i) => (i === index ? editar : dato)));
        setEditar({});
        setIndex(false);
        setNuevo({ color: "#000000" });
    }

    const panel = usePanelEdicion({ hayCambios: hayCambiosEditar || hayCambiosNuevo, onCerrar: limpiarBorradores });

    const abrirPanel = async (nombre, alAbrir) => {
        if ((hayCambiosEditar || hayCambiosNuevo) && !(await confirmarDescartar())) return;
        limpiarBorradores();
        alAbrir?.();
        panel.abrir(nombre);
    }

    const seleccionarEvento = ({ id }) => {
        const indice = data.findIndex(evento => evento._id === id);
        if (indice < 0) return;
        abrirPanel('editar', () => {
            setEditar(indice === index ? editar : data[indice]);
            setIndex(indice);
        });
    }

    const datosPreview = useMemo(() => (
        nuevo.anio !== undefined ? [...data, { ...nuevo, _id: 'nuevo' }] : data
    ), [data, nuevo]);

    const { iframeRef, src } = usePreviewSitio({
        page: 'calendario',
        ruta: '/calendario',
        data: datosPreview,
        onSelect: seleccionarEvento
    });

    const actualizarBorrador = cambios => {
        setEvento(evento => ({ ...evento, ...cambios }));
        setData(data => data.map((dato, i) => (i === index ? { ...dato, ...cambios } : dato)));
    }

    const leerDato = e => actualizarBorrador({ [e.target.name]: e.target.value });

    const leerFecha = e => {
        if (e.target.value) actualizarBorrador(deInputFecha(e.target.value));
    }

    const leerNuevo = e => setNuevo(nuevo => ({ ...nuevo, [e.target.name]: e.target.value }));

    const leerFechaNueva = e => {
        if (e.target.value) setNuevo(nuevo => ({ ...nuevo, ...deInputFecha(e.target.value) }));
    }

    const agregar = async e => {
        e.preventDefault();
        if (nuevo.anio === undefined || !nuevo.fecha || !nuevo.evento) return Swal.fire('Error!', 'Faltan datos por llenar', 'error');
        if ((await calendarioHelper.agregarEvento(nuevo))?.success) recargar();
    }

    const actualizar = async e => {
        e.preventDefault();
        if ((await calendarioHelper.editarEvento(evento, editar._id))?.success) recargar();
    }

    const eliminar = async () => {
        if ((await calendarioHelper.eliminarEvento(editar._id))?.success) recargar();
    }

    const subirPdf = e => {
        e.preventDefault();
        if (!archivoPdf) return Swal.fire('Error!', 'Selecciona un archivo PDF', 'error');
        setSubiendo(true);
        setConsulta({ consulta: 'agregarPdf', name: 'calendario_' + Date.now(), folder: 'pdfs/calendario/', file: archivoPdf, metadata: { contentDisposition: 'attachment; filename="calendario-escolar-caav.pdf"' } });
    }

    const guardarPdf = async url => {
        // useStorage solo vuelve a ejecutarse si cambia el texto de la consulta
        setConsulta('');
        if (!url) {
            setSubiendo(false);
            return Swal.fire('Error!', 'No se pudo subir el archivo', 'error');
        }
        const anterior = calendarioPdf;
        const res = await configuracionHelper.editarConfiguracion({ calendario_pdf: url });
        setSubiendo(false);
        if (res.success) {
            // Se borra hasta que la configuración ya apunta al nuevo, para que el sitio nunca quede con un enlace roto
            if (anterior.includes('/o/pdfs%2Fcalendario%2F')) {
                setConsulta({ consulta: 'eliminarSinConfirmar', urlFile: anterior });
            }
            setCalendarioPdf(url);
            setPdfActualizado(res.data?.updatedAt ?? new Date().toISOString());
            cerrarModalPdf();
        }
    }

    return (
        <Fragment>
            <SideNav />
            {subiendo && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'agregar'}
                    titulo='Agregar evento'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosNuevo}
                    pie={
                        <button type='submit' form='form_agregar_evento' className='boton_admin' disabled={!hayCambiosNuevo}>
                            Agregar evento
                        </button>
                    }
                >
                    <form id='form_agregar_evento' onSubmit={agregar}>
                        <div className="input-field">
                            <input onChange={leerFechaNueva} value={aInputFecha(nuevo)} id="fecha_dia_nuevo" type="date" />
                            <label className='active' for="fecha_dia_nuevo">Fecha</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerNuevo} value={nuevo.fecha ?? ''} id="fecha_nuevo" name='fecha' type="text" className="validate" />
                            <label className='active' for="fecha_nuevo">Fecha escrita</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerNuevo} value={nuevo.evento ?? ''} id="evento_nuevo" name='evento' type="text" className="validate" />
                            <label className='active' for="evento_nuevo">Titulo del evento</label>
                        </div>
                        <div className="input-field">
                            <p>Color</p>
                            <input onChange={leerNuevo} value={nuevo.color} id="color_nuevo" name='color' type="color" />
                        </div>
                    </form>
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar evento'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosEditar}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                Eliminar
                            </button>
                            <button type='submit' form='form_editar_evento' className='boton_admin' disabled={!hayCambiosEditar}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    <form id='form_editar_evento' onSubmit={actualizar}>
                        <div className="input-field">
                            <input onChange={leerFecha} value={aInputFecha(evento)} id="fecha_dia_editar" type="date" />
                            <label className='active' for="fecha_dia_editar">Fecha</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerDato} value={evento.fecha ?? ''} id="fecha_editar" name='fecha' type="text" className="validate" />
                            <label className='active' for="fecha_editar">Fecha escrita</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerDato} value={evento.evento ?? ''} id="evento_editar" name='evento' type="text" className="validate" />
                            <label className='active' for="evento_editar">Titulo del evento</label>
                        </div>
                        <div className="input-field">
                            <p>Color</p>
                            <input onChange={leerDato} value={evento.color ?? '#000000'} id="color_editar" name='color' type="color" />
                        </div>
                    </form>
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa del calendario'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Calendario'
                    ayuda='Haz clic en un día y luego en un evento de la lista para editarlo'
                    acciones={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={() => setModalPdf(true)}>
                                PDF del calendario
                            </button>
                            <button type='button' className='boton_admin' onClick={() => abrirPanel('agregar')}>
                                + Agregar evento
                            </button>
                        </Fragment>
                    }
                >
                    <ModalAdmin abierto={modalPdf} onCerrar={cerrarModalPdf} titulo='PDF del calendario'>
                        {calendarioPdf ? (
                            <p>
                                <a href={calendarioPdf} target="_blank" rel="noopener noreferrer">Descargar PDF actual</a>
                                {pdfActualizado && (
                                    <span style={{color:"#757575"}}> · actualizado el {new Date(pdfActualizado).toLocaleDateString('es-MX', { day:'numeric', month:'long', year:'numeric' })}</span>
                                )}
                            </p>
                        ) : (
                            <p style={{color:"#757575"}}>Aún no se ha subido un PDF; el sitio usa el archivo incluido en el código.</p>
                        )}
                        <form onSubmit={subirPdf}>
                            <div className="file-field input-field">
                                <div className="btn" style={{backgroundColor:"#00496C"}}>
                                    <span>Archivo</span>
                                    <input onChange={e => setArchivoPdf(e.target.files[0] ?? null)} type="file" accept="application/pdf" />
                                </div>
                                <div className="file-path-wrapper">
                                    <input className="file-path" type="text" placeholder="Selecciona un PDF" value={archivoPdf?.name ?? ''} readOnly />
                                </div>
                            </div>
                            {calendarioPdf && (
                                <p style={{color:"#757575", fontSize:"13px", margin:"0"}}>Al reemplazarlo, el PDF anterior se elimina y el sitio descargará el nuevo.</p>
                            )}
                            <div style={{display:"flex", justifyContent:"flex-end", gap:"10px", marginTop:"10px"}}>
                                <button type='button' className='boton_admin boton_admin_secundario' onClick={cerrarModalPdf}>
                                    Cancelar
                                </button>
                                <button type='submit' className='boton_admin' disabled={!archivoPdf}>
                                    {calendarioPdf ? 'Reemplazar PDF' : 'Subir PDF'}
                                </button>
                            </div>
                        </form>
                    </ModalAdmin>
                </PreviewSitio>
            </div>
        </Fragment>
    );
}

export default Calendario;
